<template>
    <div class="page-modal">
        <widgetModal title="Update Account Owner" :subtitle="content && content.length > 0 ?
            `Assign an account owner to ${content.length} selected account${content.length > 1 ? 's' : ''}` :
            'Assign an account owner to the selected accounts'" btnlefttext="Save Changes" btncentertext="Cancel"
            :leftloading="updating_loading" :centerloading="false" :footerhidden="false" :hiderightbtn="true"
            footer_padding="10px 18px 30px 30px" header_padding="30px 0px 0px 31px" body_padding="0px 30px"
            width="500px" @onLeftClick="onConfirm()" @onCenterClick="onCancel">
            <div class="widget-modal-body-noscoll">
                <div class="row">
                    <widgetInputWrapper title="Account Owner">
                        <searchSelectInput url="/company/search/manager" :miltiple="true" type="site"
                            :value="searchInputValue" @input="handleSearchInput" placeholder="Search account owner">
                            <template v-slot:result="{ result }">
                                <span class="search-contact-item">
                                    <img width="20" v-if="result.profile_picture"
                                        :src="$fullUrl('images/profiles/' + result.profile_picture)" />
                                    <img v-else width="20" src="@/assets/avatar.png" />
                                    {{ result.first_name | validate }}
                                    {{ result.last_name | validate }}
                                </span>
                            </template>
                            <template v-slot:selected="{ selected }">
                                <span class="selected-search-contact-item">
                                    <img width="20" v-if="selected.profile_picture"
                                        :src="$fullUrl('images/profiles/' + selected.profile_picture)" />
                                    <img v-else width="20" src="@/assets/avatar.png" />
                                    {{ selected.first_name | validate }}
                                </span>
                            </template>
                        </searchSelectInput>
                    </widgetInputWrapper>
                </div>

                <div class="row" v-if="displayOwners.length > 0">
                    <widgetInputWrapper title="Current Account Owners">
                        <div class="owners-list">
                            <div v-for="(owner, index) in displayOwners" :key="'owner-' + owner.id + '-' + index"
                                class="owner-item">
                                <div class="owner-info">
                                    <img width="24" v-if="owner.profile_picture"
                                        :src="$fullUrl('images/profiles/' + owner.profile_picture)" />
                                    <img v-else width="24" src="@/assets/avatar.png" />
                                    <span class="owner-name">
                                        {{ owner.first_name | validate }} {{ owner.last_name | validate }}
                                    </span>
                                </div>
                                <button class="remove-owner-btn" @click="removeOwner(owner)" title="Remove owner">
                                    <img width="16" src="@/assets/icon/trash-red.svg" />
                                </button>
                            </div>
                        </div>
                    </widgetInputWrapper>
                </div>
            </div>
        </widgetModal>
    </div>
</template>

<script>
export default {
    name: "update-account-owner-modal",
    props: {
        content: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            assigned: {
                added: [],
                removed: [],
            },
            initialOwnersMap: new Map(), // Track initial owners to identify removals
            updating_loading: false
        };
    },
    computed: {
        displayOwners() {
            // Display all owners currently in assigned.added
            return this.assigned.added || [];
        },
        searchInputValue() {
            // Return only newly added owners (without on_update flag) for search input
            return {
                added: this.assigned.added.filter(owner => !owner.on_update),
                removed: []
            };
        }
    },
    mounted() {
        let vm = this;
        vm.loadExistingOwners();
    },
    methods: {
        loadExistingOwners() {
            let vm = this;
            // Collect all unique owners from selected accounts
            const ownersMap = new Map();

            vm.content.forEach(account => {
                if (account.owners && Array.isArray(account.owners)) {
                    account.owners.forEach(owner => {
                        // Use owner id as key to avoid duplicates
                        if (owner.id && !ownersMap.has(owner.id)) {
                            // Create a copy with on_update flag to track removals
                            const ownerCopy = {
                                ...owner,
                                on_update: true
                            };
                            ownersMap.set(owner.id, ownerCopy);
                            // Store in initialOwnersMap to track which ones were originally present
                            vm.initialOwnersMap.set(owner.id, true);
                        }
                    });
                }
            });

            // Populate assigned.added with existing owners
            vm.assigned.added = Array.from(ownersMap.values());
        },
        removeOwner(owner) {
            let vm = this;

            // Find and remove from assigned.added
            const addedIndex = vm.assigned.added.findIndex(item => item.id === owner.id);
            if (addedIndex !== -1) {
                const removedOwner = vm.assigned.added.splice(addedIndex, 1)[0];

                // If it was an initial owner (exists in initialOwnersMap), add to removed array
                if (vm.initialOwnersMap.has(removedOwner.id)) {
                    // Check if it's not already in removed array
                    if (!vm.assigned.removed.includes(removedOwner.id)) {
                        vm.assigned.removed.push(removedOwner.id);
                    }
                }
            }
        },
        onConfirm() {
            let vm = this;

            // Filter out existing owners (those with on_update flag) - only send newly added owners
            const newlyAddedOwners = vm.assigned.added.filter((item) => !item.on_update);

            // Check if there are any changes (newly added or removed owners)
            if (newlyAddedOwners.length === 0 && vm.assigned.removed.length === 0) {
                vm.$notify({
                    group: "status",
                    title: "Message",
                    text: "Please add or remove at least one account owner",
                    type: "warn",
                    duration: 3000,
                });
                return;
            }

            vm.updating_loading = true;

            // Get IDs of selected accounts
            const accountIds = vm.content.map(account => account.id);

            // Prepare account owner data - only send newly added owners
            const accountOwner = newlyAddedOwners.length > 0 ? JSON.stringify(newlyAddedOwners) : JSON.stringify([]);
            const accountOwnerRemoved = vm.assigned.removed.length > 0 ? vm.assigned.removed : [];

            vm.$store
                .dispatch("ACCOUNT_UPDATE_OWNER", {
                    ids: accountIds,
                    account_owner: accountOwner,
                    account_owner_removed: accountOwnerRemoved
                })
                .then((response) => {
                    vm.updating_loading = false;
                    vm.$notify({
                        group: "status",
                        title: "Message",
                        text: "Account owner updated successfully",
                        type: "success",
                        duration: 3000,
                    });
                    vm.$emit("success");
                })
                .catch((error) => {
                    vm.updating_loading = false;
                    vm.$notify({
                        group: "status",
                        title: "Message",
                        text: "Something went wrong",
                        type: "warn",
                        duration: 3000,
                    });
                    console.error({ error });
                });
        },
        handleSearchInput(value) {
            let vm = this;
            // Merge search input changes with existing owners
            // Keep all existing owners (with on_update flag)
            const existingOwners = vm.assigned.added.filter(owner => owner.on_update === true);
            // Get newly added/removed owners from search input
            const newOwners = value.added || [];

            // Merge: existing owners + new owners from search
            const merged = [...existingOwners, ...newOwners];

            // Update assigned.added
            vm.assigned.added = merged;
        },
        onCancel() {
            this.$emit("close");
        }
    }
};
</script>

<style lang="scss" scoped>
.page-modal {
    .widget-modal-body-noscoll {
        .owners-list {
            display: flex;
            flex-direction: column;
            gap: 8px;
            max-height: 300px;
            overflow-y: auto;
            padding: 10px;
            background: #f7f7f7;
            border-radius: 5px;
            margin-top: 10px;

            .owner-item {
                display: flex;
                align-items: center;
                justify-content: space-between;
                padding: 8px 12px;
                background: #fff;
                border-radius: 4px;
                border: 1px solid #e0e0e0;

                .owner-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex: 1;

                    img {
                        border-radius: 50%;
                        object-fit: cover;
                    }

                    .owner-name {
                        font-size: 14px;
                        color: #333;
                    }
                }

                .remove-owner-btn {
                    background: transparent;
                    border: none;
                    cursor: pointer;
                    padding: 4px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border-radius: 4px;
                    transition: background-color 0.2s;

                    &:hover {
                        background: #ffe0e0;
                    }

                    img {
                        display: block;
                    }
                }
            }
        }
    }
}
</style>